import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import ArenaxGameCard from './ArenaxGameCard';
import { Sparkles, Plus, Filter } from 'lucide-react';

const SPORTS = ['Football','Cricket','Badminton','Tennis','Basketball','Pickleball','Volleyball','Table Tennis'];
const SKILLS = ['beginner','intermediate','advanced','pro'];

export default function ArenaxGames() {
  const [params, setParams] = useSearchParams();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const sport = params.get('sport') || '';
  const skill = params.get('skill') || '';
  const city = params.get('city') || '';

  useEffect(() => {
    const q = new URLSearchParams();
    if (sport) q.set('sport', sport);
    if (skill) q.set('skill_level', skill);
    if (city) q.set('city', city);
    setLoading(true);
    api.get(`/arenax/games?${q.toString()}`).then(r => setGames(r.data.games || [])).finally(() => setLoading(false));
  }, [sport, skill, city]);

  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  return (
    <ArenaxLayout>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid="arenax-games-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
          <div>
            <div className="text-[11px] tracking-[0.4em] text-cyan-300 font-bold mb-2">PUBLIC GAMES</div>
            <h1 className="text-3xl sm:text-4xl font-black text-slate-100">Find your game</h1>
            <p className="text-sm text-slate-400 mt-1">Open games across India. Join in one tap.</p>
          </div>
          <Link to="/arenax/games/host" data-testid="arenax-games-host-cta">
            <button className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-950 flex items-center gap-2"
              style={{ background: 'var(--ax-primary-gradient)' }}>
              <Plus className="h-4 w-4" />Host a Game
            </button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="arenax-games-filters">
          <Filter className="h-3.5 w-3.5 text-slate-500" />
          <FilterChip label="All sports" active={!sport} onClick={() => setFilter('sport', '')} />
          {SPORTS.map(s => <FilterChip key={s} label={s} active={sport === s} onClick={() => setFilter('sport', s)} />)}
          <div className="w-px h-5 bg-slate-700 mx-1" />
          <FilterChip label="All skill" active={!skill} onClick={() => setFilter('skill', '')} />
          {SKILLS.map(sk => <FilterChip key={sk} label={sk} active={skill === sk} onClick={() => setFilter('skill', sk)} />)}
        </div>

        {loading ? (
          <div className="text-center text-slate-500 text-sm py-12">Loading...</div>
        ) : games.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-cyan-500/20 p-14 text-center">
            <Sparkles className="h-10 w-10 text-cyan-400/50 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-200 mb-2">No games match your filter yet</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto mb-5">Try widening your filters — or be the host that creates the first game in this slot.</p>
            <Link to="/arenax/games/host"><button className="px-5 py-2.5 rounded-xl text-xs font-bold text-slate-950" style={{ background: 'var(--ax-primary-gradient)' }}>Host a Game</button></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="arenax-games-grid">
            {games.map(g => <ArenaxGameCard key={g.id} game={g} />)}
          </div>
        )}
      </section>
    </ArenaxLayout>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all capitalize ${
        active ? 'bg-cyan-500 text-slate-950 shadow-md' : 'bg-slate-900 text-slate-300 border border-slate-700 hover:border-cyan-500/50'
      }`}>{label}</button>
  );
}
